import { defaults, LABELS } from "../engine/registry";
export const clone = (p) => JSON.parse(JSON.stringify(p));
export function serialize(p) {
  return JSON.stringify(p, null, 2);
}
export function deserialize(text) {
  if (text.length > 8 * 1024 * 1024)
    throw new Error("Project is too large (8 MB maximum).");
  let p;
  try {
    p = JSON.parse(text);
  } catch {
    throw new Error("This is not a valid JSON project.");
  }
  if (!p || p.schemaVersion !== 3)
    throw new Error(
      "Unsupported project version. Open a Timing Diagram Generator V3 JSON file.",
    );
  for (const k of ["components", "connections", "inputs", "observables"])
    if (!Array.isArray(p[k])) throw new Error(`Project is missing ${k}.`);
  if (
    p.components.length > 200 ||
    p.connections.length > 5000 ||
    p.inputs.length > 128
  )
    throw new Error(
      "Project exceeds the safety limit of 200 components, 5,000 wires or 128 terminals.",
    );
  const str = (v) => typeof v === "string" && v.length > 0 && v.length <= 160;
  if (
    p.components.some(
      (c) =>
        !c ||
        !str(c.id) ||
        !str(c.name) ||
        !Object.prototype.hasOwnProperty.call(LABELS, c.type) ||
        !Number.isFinite(c.x) ||
        !Number.isFinite(c.y) ||
        !c.config ||
        typeof c.config !== "object" ||
        Array.isArray(c.config) ||
        (c.config.output !== undefined && typeof c.config.output !== "string"),
    )
  )
    throw new Error("Invalid component data.");
  const point = (q) => q && Number.isFinite(q.x) && Number.isFinite(q.y);
  if (
    p.components.some(
      (c) =>
        (c.size &&
          (!Number.isFinite(c.size.w) ||
            !Number.isFinite(c.size.h) ||
            c.size.w < 20 ||
            c.size.h < 20 ||
            c.size.w > 800 ||
            c.size.h > 1200)) ||
        (c.config.pinLabels &&
          (typeof c.config.pinLabels !== "object" ||
            Object.values(c.config.pinLabels).some(
              (v) => typeof v !== "string" || v.length > 40,
            ))),
    )
  )
    throw new Error("Invalid component dimensions or terminal labels.");
  if (
    p.connections.some(
      (e) =>
        (e.waypoints &&
          (!Array.isArray(e.waypoints) ||
            e.waypoints.length > 400 ||
            e.waypoints.some((q) => !point(q)))) ||
        (e.terminal && !point(e.terminal)) ||
        (e.anchors && (!point(e.anchors.start) || !point(e.anchors.end))),
    )
  )
    throw new Error("Invalid wire bend coordinates.");
  const source = (s) =>
    s &&
    typeof s === "object" &&
    (str(s.input) ||
      (str(s.component) && str(s.pin)) ||
      [0, 1, "X"].includes(s.constant));
  if (
    p.connections.some(
      (e) =>
        !e ||
        !str(e.id) ||
        !source(e.from) ||
        !str(e.to?.component) ||
        !str(e.to?.pin),
    )
  )
    throw new Error("Invalid wire data.");
  if (
    p.inputs.some(
      (i) =>
        !i ||
        !str(i.id) ||
        !str(i.name) ||
        !Array.isArray(i.values) ||
        (i.events !== undefined &&
          (!Array.isArray(i.events) ||
            i.events.some((e) => !e || !Number.isFinite(e.time)))),
    )
  )
    throw new Error("Invalid input waveform data.");
  if (p.observables.some((o) => !o || !str(o.id) || !source(o.source)))
    throw new Error("Invalid observable data.");
  if (
    !p.timing ||
    !Number.isFinite(p.timing.duration) ||
    !Number.isFinite(p.timing.step)
  )
    throw new Error("Invalid timing configuration.");
  p.components = p.components.map((c) => ({
    ...c,
    config: { ...defaults(c.type), ...c.config },
  }));
  p.timing = {
    showClocks: true,
    format: "binary",
    order: [],
    hidden: [],
    aliases: {},
    ...p.timing,
  };
  if (
    !Array.isArray(p.timing.hidden) ||
    !Array.isArray(p.timing.order) ||
    !p.timing.aliases ||
    typeof p.timing.aliases !== "object" ||
    Object.values(p.timing.aliases).some((v) => typeof v !== "string")
  )
    throw new Error("Invalid display settings.");
  if(p.timing.blankSheet){
    const sheet=p.timing.blankSheet;
    if((sheet.clockName!==undefined&&!str(sheet.clockName))||!Number.isInteger(sheet.cycles)||sheet.cycles<1||sheet.cycles>128||![0,1].includes(sheet.clockStart)||!Array.isArray(sheet.rows)||sheet.rows.length>64||sheet.rows.some(r=>!r||!str(r.id)||!str(r.name)||typeof r.given!=='boolean'||(r.initial!==undefined&&r.initial!==null&&![0,1].includes(r.initial))||!Array.isArray(r.values)||r.values.length>256||r.values.some(v=>![0,1,'X'].includes(v)))||new Set(sheet.rows.map(r=>r.id)).size!==sheet.rows.length||new Set(sheet.rows.map(r=>r.name)).size!==sheet.rows.length)throw new Error('Invalid blank-sheet variables.');
  }
  p.metadata = {
    title: "Circuit exercise",
    seed: "260",
    ...(p.metadata || {}),
  };
  if (
    typeof p.metadata.title !== "string" ||
    !["string", "number"].includes(typeof p.metadata.seed)
  )
    throw new Error("Invalid project metadata.");
  if (p.metadata.generator) {
    const g = p.metadata.generator;
    if (
      !g.counts ||
      typeof g.counts !== "object" ||
      !g.options ||
      !Array.isArray(g.clocks) ||
      Object.entries(g.counts).some(
        ([t, n]) =>
          !Object.prototype.hasOwnProperty.call(LABELS, t) ||
          !Number.isInteger(n) ||
          n < 0 ||
          n > 24,
      )
    )
      throw new Error("Invalid saved generation settings.");
  }
  return p;
}
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const downloadProject = (p) =>
  saveBlob(
    new Blob([serialize(p)], { type: "application/json" }),
    "timing-diagram-v3.json",
  );
